export function generateId(prefix = 'feedback') {
	const timestamp = Date.now();
	const random = Math.random().toString(36).substring(2, 9);
	return `${prefix}_${timestamp}_${random}`;
}

export function deepMerge(target, source) {
	const result = { ...target };

	for (const key in source) {
		if (source.hasOwnProperty(key)) {
			if (
				source[key] &&
				typeof source[key] === 'object' &&
				!Array.isArray(source[key])
			) {
				result[key] = deepMerge(target[key] || {}, source[key]);
			} else {
				result[key] = source[key];
			}
		}
	}

	return result;
}

export function debounce(func, wait) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}

export function throttle(func, limit) {
	let lastFunc;
	let lastRan;
	return function (...args) {
		if (!lastRan) {
			func(...args);
			lastRan = Date.now();
		} else {
			clearTimeout(lastFunc);
			lastFunc = setTimeout(
				() => {
					if (Date.now() - lastRan >= limit) {
						func(...args);
						lastRan = Date.now();
					}
				},
				limit - (Date.now() - lastRan)
			);
		}
	};
}

export function isValidEmail(email) {
	if (!email || typeof email !== 'string') return false;

	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email.trim());
}

export function sanitizeHTML(str) {
	if (!str || typeof str !== 'string') return '';

	const div = document.createElement('div');
	div.textContent = str;
	return div.innerHTML;
}

export function getCSSProperty(element, property, fallback = '') {
	if (!element || !property) return fallback;

	try {
		const style = window.getComputedStyle(element);
		return style.getPropertyValue(property) || fallback;
	} catch (error) {
		return fallback;
	}
}

export function isInViewport(element) {
	if (!element) return false;

	const rect = element.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <=
			(window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
}

export function scrollToElement(element, options = {}) {
	if (!element) return;

	const defaultOptions = {
		behavior: 'smooth',
		block: 'center',
		inline: 'nearest',
	};

	element.scrollIntoView({ ...defaultOptions, ...options });
}

export function getBrowserInfo() {
	const userAgent = navigator.userAgent;
	const platform = navigator.platform;

	return {
		userAgent,
		platform,
		language: navigator.language || navigator.userLanguage,
		cookieEnabled: navigator.cookieEnabled,
		screenResolution: `${screen.width}x${screen.height}`,
		windowSize: `${window.innerWidth}x${window.innerHeight}`,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	};
}

export function formatFileSize(bytes) {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function delay(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export function safeJsonParse(str, fallback = null) {
	try {
		return JSON.parse(str);
	} catch (error) {
		return fallback;
	}
}

export function escapeRegex(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function getNestedProperty(obj, path, defaultValue = undefined) {
	if (!obj || !path) return defaultValue;

	const keys = path.split('.');
	let current = obj;

	for (const key of keys) {
		if (current === null || current === undefined || !(key in current)) {
			return defaultValue;
		}
		current = current[key];
	}

	return current;
}

export function setNestedProperty(obj, path, value) {
	if (!obj || !path) return obj;

	const keys = path.split('.');
	const lastKey = keys.pop();
	let current = obj;

	for (const key of keys) {
		if (!(key in current) || typeof current[key] !== 'object') {
			current[key] = {};
		}
		current = current[key];
	}

	current[lastKey] = value;
	return obj;
}

export function isBrowser() {
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

export function isMobile() {
	if (!isBrowser()) return false;

	return (
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			navigator.userAgent
		) || window.innerWidth <= 768
	);
}

export function getCurrentTimestamp() {
	return new Date().toISOString();
}

export function validateConfig(config, required = []) {
	const missing = [];

	for (const key of required) {
		if (!config[key]) {
			missing.push(key);
		}
	}

	if (missing.length > 0) {
		throw new Error(`Missing required configuration: ${missing.join(', ')}`);
	}

	return true;
}
